import React from "react";
import { GatsbyImage, getImage } from "gatsby-plugin-image";
import { Link as ScrollLink } from "react-scroll";
import css from "@styled-system/css";

import {
  AspectRatio,
  Box,
  Button,
  Column,
  Columns,
  FadeWrapper,
  Heading,
  Link,
  RichText,
  Spacer,
  Text,
  VStack,
  Wrapper,
} from "components";

function Hero({
  button_label,
  button_link,
  hero_image,
  label,
  headline,
  introduction,
  quick_links,
}) {
  return (
    <Box as="section">
      {/* Hero Image */}
      <AspectRatio ratio={{ _: "3:2", lg: "3:1" }}>
        {hero_image && hero_image.gatsbyImageData && (
          <GatsbyImage
            image={getImage(hero_image.gatsbyImageData)}
            alt={hero_image.alt || ""}
            style={{ width: "100%", height: "100%" }}
            imgStyle={{ objectFit: "cover", objectPosition: "50% 100%" }}
          />
        )}
        {/* Soft top scrim */}
        <Box
          position="absolute"
          top={0}
          left={0}
          width="100%"
          height="100%"
          opacity={0.275}
          css={{
            background: "linear-gradient(to bottom, #000, transparent)",
          }}
        />
        {/* Solid angle bottom scrim */}
        <Box
          position="absolute"
          bottom={0}
          left={0}
          width="100%"
          pb="10%"
          css={{
            background: "linear-gradient(-4deg, #FFF 49.5%, transparent 50%)",
          }}
        />
      </AspectRatio>

      {/* Content */}
      <Wrapper as="div" pt="layout.4" pb={{ _: "layout.3", md: "layout.4" }}>
        <VStack space={{ _: "layout.3", md: "layout.4" }}>
          {/* Label */}
          {label && label.text && (
            <Columns spaceX="layout.5" alignX="end">
              <Column width={{ _: 1 / 1, md: 8 / 12 }}>
                <FadeWrapper delay={0.2}>
                  <Heading
                    children={label.text}
                    as="h1"
                    size={900}
                    maxWidth="16ch"
                  />
                </FadeWrapper>
              </Column>
            </Columns>
          )}

          <Columns flexWrap="wrap-reverse" spaceX="layout.5" spaceY="layout.3">
            <Column width={{ _: 1, md: 4 / 12 }}>
              <FadeWrapper delay={0.4}>
                {quick_links && quick_links[0] && quick_links[0].quick_link && (
                  <Heading
                    children="Jump To"
                    as="span"
                    color="label.0"
                    size={100}
                  />
                )}
                <Spacer spaceY={{ _: "layout.2", md: "layout.3" }} />
                {/* Jump To Links */}
                {quick_links &&
                  quick_links[0] &&
                  (quick_links[0].quick_link ||
                    quick_links[0].external_link) && (
                    <VStack as="ul" space="layout.1">
                      {quick_links.map(({ external_link, quick_link }, i) => {
                        // Render external and in-page anchor links with the same
                        // wrapper (anchor → Text) so they stack with even spacing.
                        const linkText = (
                          <Text
                            children={quick_link}
                            size={500}
                            color="label.1"
                            css={css({
                              cursor: "pointer",
                              transition: "color 200ms ease-out",
                              "&:hover": {
                                color: "label.0",
                              },
                            })}
                          />
                        );

                        return external_link && external_link.url ? (
                          <Link href={external_link.url} key={i}>
                            {linkText}
                          </Link>
                        ) : (
                          <ScrollLink
                            offset={-128}
                            to={quick_link}
                            smooth={true}
                            key={i}
                          >
                            {linkText}
                          </ScrollLink>
                        );
                      })}
                    </VStack>
                  )}
              </FadeWrapper>
            </Column>
            <Column width={{ _: 1 / 1, md: 8 / 12 }}>
              <FadeWrapper delay={0.4}>
                <VStack space={{ _: "layout.3", md: "layout.4" }}>
                  {/* Headline */}
                  {headline && headline.text && (
                    <Text
                      children={headline.text}
                      as="h2"
                      size={700}
                      maxWidth="32ch"
                    />
                  )}
                  {/* Introduction */}
                  {introduction && introduction.html && (
                    <RichText
                      html={introduction.html}
                      size={500}
                      maxWidth="65ch"
                    />
                  )}
                  {/* Optional Button */}
                  {button_link && button_link.url && button_label && (
                    <Link href={button_link.url}>
                      <Button
                        children={button_label}
                        appearance="primary"
                        intent="secondary"
                        round={true}
                      />
                    </Link>
                  )}
                </VStack>
              </FadeWrapper>
            </Column>
          </Columns>
        </VStack>
      </Wrapper>
    </Box>
  );
}

export default Hero;
